/* live_v2/tween.js — number tweening + change-pulse for "alive" feel.
 *
 * Usage in a partial:
 *   <span x-data="LSTween.num(function() { return this.bestLineupTotal; })"
 *         :class="{ 'ls-tween-pulse': pulse }"
 *         x-text="display"></span>
 *
 * The source function is called with `this` = the inline x-data scope,
 * which inherits the parent's properties via Alpine's scope chain — so
 * `this.bestLineupTotal` walks up to the host controller getter.
 *
 * On every change of the source value the tweened display animates from
 * the previous value to the new one over `dur` ms with an ease-out cubic
 * curve. `pulse` becomes true for 800ms so the partial can flash a
 * background highlight. Both reactive — Alpine re-renders automatically.
 */
window.LSTween = window.LSTween || {
  /** Tweening number factory. `srcFn` is a regular function returning the
   *  current value; `opts` may override `decimals` (default 2) and `dur`
   *  (default 600ms). Returns an Alpine x-data object. */
  /** Event glow factory. Watches a key function and sets `flash: true`
   *  for ~1500ms whenever the key changes. Distinct from `num()` because
   *  it fires on DISCRETE events only (HR scored, top-scorer identity
   *  swap, status flip) — not on every continuous tick. Templates should
   *  bind `:class="{'ls-card-glow': flash}"` on the card root. Skips the
   *  first observed value so cards don't all glow on mount. */
  event(keyFn, opts) {
    const dur = (opts && opts.dur != null) ? opts.dur : 1500;
    return {
      _lastKey:    null,
      _eventTimer: null,
      flash:       false,
      get _eventKey() { return keyFn.call(this); },
      init() {
        this._lastKey = this._eventKey;
        this.$watch('_eventKey', (next) => {
          if (next === this._lastKey) return;
          this._lastKey = next;
          this.flash = true;
          clearTimeout(this._eventTimer);
          this._eventTimer = setTimeout(() => { this.flash = false; }, dur);
        });
      },
    };
  },

  /** Volume moment factory (v4 KPI audit — "loud when it matters" as a
   *  decaying MOMENT, not a permanent level). Watches a value; when it rises
   *  by >= momentAt it fires a hot moment: `flash` true for the card glow
   *  (~1500ms) and `vol` = 'is-hot' for hotMs, then it relaxes to 'is-live'
   *  (calm green) while value >= liveAt, then '' (quiet). The absolute-
   *  threshold kpiVolume() left a tile breathing orange for the whole slate
   *  after one 2-HR night; this only shouts right after something happens.
   *
   *  opts: { liveAt=1, momentAt=1, hotMs=24000 }. Count tiles use momentAt 1
   *  (any +1 is a moment); continuous tiles set momentAt to a meaningful jump.
   *  Set momentAt very high (e.g. 1e9) for a live-only tile that never goes hot.
   *  Same no-render-side-effects contract as num()/event(): `vol` only READS
   *  state + Date.now(); a timer bumps the reactive `_tick` when the hot
   *  window lapses so Alpine re-evaluates. */
  volume(valueFn, opts) {
    opts = opts || {};
    const liveAt   = opts.liveAt   != null ? opts.liveAt   : 1;
    const momentAt = opts.momentAt != null ? opts.momentAt : 1;
    const hotMs    = opts.hotMs    != null ? opts.hotMs    : 24000;
    return {
      _lastV:      0,
      _hotUntil:   0,
      _tick:       0,        // reactive nudge so `vol` re-renders on decay
      _volTimer:   null,
      flash:       false,    // card-glow, same as event()
      _flashTimer: null,
      get _v() { return valueFn.call(this) || 0; },
      get vol() {
        this._tick;          // dependency: re-evaluate when the timer bumps it
        if (Date.now() < this._hotUntil) return 'is-hot';
        return this._v >= liveAt ? 'is-live' : '';
      },
      init() {
        this._lastV = this._v;
        // A moment is something that changed WHILE YOU WERE WATCHING. The board
        // mounts behind _switching/_awaitFirstPush, so _v is 0 until the first
        // payload commits - and without this arming window that payload simply
        // ARRIVING read as a 0 -> 40.82 jump, cleared momentAt, and painted a
        // 25-second "something is happening" alarm on every load and every
        // slate switch, on a board where nothing was live. The tile beside it
        // said "no live action yet" at the same time (web audit run 1, D2).
        //
        // Armed by elapsed time rather than by a first-fire flag on purpose: a
        // board that legitimately loads on 0 and takes its first score a minute
        // later must still fire, and a flag would have eaten that one.
        // A fixed window off mount was not enough on its own: a board that
        // takes longer than the window to build lands its payload after the
        // alarm is armed, and fires anyway (seen intermittently on dark). So
        // arm off the FIRST change instead - that is the payload, whenever it
        // arrives - and cap it, because a board can legitimately sit on 0 and
        // take its first score minutes later, which IS a moment and must fire.
        const _mounted = Date.now();
        this._armAt = 0;
        this.$watch('_v', (next) => {
          const from = this._lastV;
          this._lastV = next;
          if (!this._armAt && Date.now() - _mounted < 20000) {
            this._armAt = Date.now() + 1500;    // this one is the baseline
            return;
          }
          if (Date.now() < this._armAt) return;  // the payload landing is not a moment
          if (next - from < momentAt) return;   // not an upward moment
          this._hotUntil = Date.now() + hotMs;
          this.flash = true;
          clearTimeout(this._flashTimer);
          this._flashTimer = setTimeout(() => { this.flash = false; }, 1500);
          clearTimeout(this._volTimer);
          this._volTimer = setTimeout(() => { this._tick++; }, hotMs + 60);
        });
      },
    };
  },

  num(srcFn, opts) {
    const decimals = (opts && opts.decimals != null) ? opts.decimals : 2;
    const dur      = (opts && opts.dur      != null) ? opts.dur      : 600;
    return {
      _v:     0,        // currently displayed (animated) value
      _last:  0,        // last "settled" target
      pulse:  false,    // brief true on each change
      // _target is a reactive getter so $watch('_target') fires whenever
      // the source value changes. srcFn is called with the x-data `this`
      // so it can reach parent state via Alpine's scope chain.
      get _target() { return srcFn.call(this) || 0; },
      // Public:
      get display() { return this._v.toFixed(decimals); },
      init() {
        const v = this._target;
        this._v    = v;
        this._last = v;
        this.$watch('_target', (next) => {
          if (next === this._last) return;
          // From the value ON SCREEN, and the earlier count-up stopped: two
          // loops raced and a frame showed 76.25 between 108.54 and 122.50,
          // a negative spread (run 2, LIVE-1). Reduced motion: no count-up.
          const from = this._v;
          this._last = next;
          this._run = (this._run || 0) + 1;
          const run = this._run;
          if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this._v = next; return; }
          // Sign-aware flash: a rise flashes accent (a gain), a drop flashes
          // neutral slate — a falling total (benching, negative pitcher line)
          // shouldn't read as celebratory green. Set on this span so the
          // keyframe's var(--pulse-rgb) picks it up; no template change.
          if (this.$el && this.$el.style) {
            this.$el.style.setProperty(
              '--pulse-rgb',
              next < from ? 'var(--ls-ink-muted-rgb)' : 'var(--ls-accent-rgb)'
            );
          }
          // Flash the cell.
          this.pulse = true;
          setTimeout(() => { this.pulse = false; }, 800);
          // RAF count-up. Ease-out cubic feels punchier than linear.
          const start = performance.now();
          const step = (now) => {
            if (run !== this._run) return;   // a newer change took over
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            this._v = from + (next - from) * eased;
            if (t < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      },
    };
  },
};

/* LSMerge — compose multiple Alpine x-data objects without losing
 * getters or init() side-effects. Object.assign / spread invoke getters
 * once and freeze the return (locked memory feedback_alpine_spread_
 * kills_getters.md), so merging a widget factory with `LSTween.event(...)`
 * requires getOwnPropertyDescriptors. Init functions are chained so each
 * mix-in still wires its $watch / lifecycle. */
window.LSMerge = window.LSMerge || function(...objs) {
  const inits = [];
  const target = {};
  for (const o of objs) {
    if (!o) continue;
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(o));
    if (typeof o.init === 'function') inits.push(o.init);
  }
  if (inits.length) {
    target.init = function() { inits.forEach(fn => fn.call(this)); };
  }
  return target;
};

/* LSGlow.tick — imperative companion to LSTween.event for places where
 * nesting an Alpine x-data isn't viable (e.g. inside an x-for over game
 * cards — Alpine memory feedback_alpine_xdata_xfor_scope.md). Call from
 * x-effect with a stable key string; the helper toggles `.ls-card-glow`
 * on the element only when the key changes. Skips the first observed
 * value so cards don't glow on mount. */
window.LSGlow = window.LSGlow || {
  tick(el, key, opts) {
    if (!el) return;
    const dur = (opts && opts.dur != null) ? opts.dur : 1500;
    if (el._lsLastKey === undefined) { el._lsLastKey = key; return; }
    if (el._lsLastKey === key) return;
    el._lsLastKey = key;
    el.classList.remove('ls-card-glow');
    void el.offsetWidth;
    el.classList.add('ls-card-glow');
    clearTimeout(el._lsGlowTimer);
    el._lsGlowTimer = setTimeout(() => {
      el.classList.remove('ls-card-glow');
    }, dur);
  },
};
